/**
 * MealSelector component
 * Modal for selecting a meal with search and filter capabilities
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMeals } from '@/hooks/useMeals';
import { useAvailabilityForDate } from '@/hooks/useAvailabilityForDate';
import { useSmartMenuSuggestions } from '@/hooks/useSmartMenuSuggestions';
import type { Meal, MealType, AvailabilitySummary } from '@/types/api';
import { format } from 'date-fns';
import { capitalize } from '@/lib/utils/text';

export interface MealSelectorProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Callback when meal is selected */
  onSelect: (meal: Meal) => void;
  /** Currently selected meal (for highlighting) */
  selectedMealId?: number;
  /** Modal title */
  title?: string;
  /** Date for context (YYYY-MM-DD) - required for smart suggestions */
  date: string;
  /** Meal type for context - required for smart suggestions */
  mealType: MealType;
  /** Availability summary data for the week */
  availabilitySummary?: AvailabilitySummary[];
}

/**
 * Modal component for selecting a meal with smart suggestions
 *
 * Features:
 * - Smart suggestions sorted by family preference score
 * - Search meals by name
 * - Keyboard navigation (Escape to close)
 * - Click outside to close
 * - Shows meal tags
 * - Highlights currently selected meal
 * - Displays selection counts per present users
 * - Shows "already in menu this week" badge
 * - Shows who's eating for the selected meal slot
 *
 * @example
 * ```tsx
 * function MenuSlot({ date, mealType }: { date: string; mealType: MealType }) {
 *   const [isOpen, setIsOpen] = useState(false);
 *
 *   const handleSelect = (meal: Meal) => {
 *     console.log('Selected meal:', meal);
 *     setIsOpen(false);
 *   };
 *
 *   return (
 *     <>
 *       <button onClick={() => setIsOpen(true)}>Select Meal</button>
 *       <MealSelector
 *         isOpen={isOpen}
 *         onClose={() => setIsOpen(false)}
 *         onSelect={handleSelect}
 *         selectedMealId={123}
 *         date={date}
 *         mealType={mealType}
 *       />
 *     </>
 *   );
 * }
 * ```
 */
export function MealSelector({
  isOpen,
  onClose,
  onSelect,
  selectedMealId,
  title,
  date,
  mealType,
  availabilitySummary,
}: MealSelectorProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const defaultTitle = title || t('menu.selectMeal');

  // Fetch smart suggestions (pre-sorted by backend)
  const { data: suggestions = [], isLoading: suggestionsLoading } = useSmartMenuSuggestions(
    date,
    mealType
  );

  // Fetch all meals with search query (fallback if needed)
  const { data: mealsData, isLoading: mealsLoading, error } = useMeals({
    query: searchQuery || undefined,
    limit: 50,
  });

  const isLoading = suggestionsLoading || mealsLoading;

  // Get availability for the specific date/meal slot
  const availability = useAvailabilityForDate({
    date,
    mealType,
    availabilitySummary,
    enabled: !!(date && mealType && availabilitySummary),
  });

  // Create a map for quick suggestion lookup
  const suggestionsMap = useMemo(() => {
    const map = new Map();
    suggestions.forEach((suggestion) => {
      map.set(suggestion.id, suggestion);
    });
    return map;
  }, [suggestions]);

  // Use suggestions as the primary data source (already sorted by backend)
  // Filter by search query if needed
  const displayedMeals = useMemo(() => {
    if (searchQuery) {
      // When searching, use the meals API results
      return mealsData?.meals || [];
    }
    // Otherwise use smart suggestions (already sorted by score)
    return suggestions;
  }, [searchQuery, mealsData?.meals, suggestions]);

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isOpen &&
        modalRef.current &&
        !modalRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleMealClick = (meal: Meal) => {
    onSelect(meal);
  };

  const handleClearSelection = () => {
    onSelect({
      id: 0,
      name: '',
      tags: [],
      repeatIntervalWeeks: 3,
      createdAt: '',
    } as Meal);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{defaultTitle}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label={t('menu.closeModal')}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Top banner with smart suggestions info */}
        {availability && availability.presentCount > 0 && (
          <div className="bg-primary-50 border-b border-primary-100 px-6 py-3">
            <div className="flex items-center gap-2 text-sm text-primary-800">
              <svg
                className="w-5 h-5 text-primary-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                />
              </svg>
              <span className="font-medium">
                {t('menu.smartSuggestionsEnabled')}
              </span>
            </div>
          </div>
        )}

        {/* Availability section - shows who's eating this meal */}
        {date && mealType && availability && availability.totalUsers > 0 && (
          <div className="bg-blue-50 border-b border-blue-200 px-6 py-4">
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <svg
                className="w-5 h-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              {t('menu.whosEating', {
                mealType: mealType === 'lunch' ? t('availability.lunch') : t('availability.dinner'),
                date: format(new Date(date + 'T00:00:00'), 'EEEE, MMM d')
              })}
            </h3>
            {availability.presentCount > 0 ? (
              <>
                <div className="flex flex-wrap gap-2 mb-3">
                  {availability.presentUsers.map((user) => (
                    <span
                      key={user.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-blue-100 text-blue-900 border border-blue-300"
                    >
                      {user.name}
                      {user.isOrganizer && (
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-200 text-yellow-900"
                          title={t('header.organizer')}
                          aria-label={t('header.organizer')}
                        >
                          {t('header.organizer')}
                        </span>
                      )}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-blue-700">
                  {t('menu.usersAvailable', {
                    present: availability.presentCount,
                    total: availability.totalUsers
                  })}
                </p>
              </>
            ) : (
              <p className="text-sm text-blue-700 italic">
                {t('menu.noAvailability')}
              </p>
            )}
          </div>
        )}

        {/* Search */}
        <div className="p-6 border-b border-gray-200">
          <input
            ref={searchInputRef}
            type="text"
            placeholder={t('menu.searchMeals')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Meal list */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-red-600">
                {t('menu.errorLoading')}
              </p>
            </div>
          )}

          {!isLoading && !error && displayedMeals.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {searchQuery
                  ? t('menu.noMealsFound', { query: searchQuery })
                  : t('menu.noMealsAvailable')}
              </p>
            </div>
          )}

          {!isLoading && !error && displayedMeals.length > 0 && (
            <div className="space-y-2">
              {displayedMeals.map((meal) => {
                const isSelected = meal.id === selectedMealId;
                const visibleTags = meal.tags.filter((tag) => !tag.isHidden);
                // Get suggestion data if available
                const suggestion = suggestionsMap.get(meal.id);

                return (
                  <button
                    key={meal.id}
                    type="button"
                    onClick={() => handleMealClick(meal)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-gray-900">
                            {capitalize(meal.name)}
                          </h3>
                        </div>

                        {/* Show selection metadata if available */}
                        {suggestion && suggestion.selectionCount > 0 && (
                          <div className="text-sm text-gray-600 mb-2">
                            {suggestion.selectionCount}/{suggestion.totalPresent} {t('menu.personPresent')}
                          </div>
                        )}

                        {/* Show "already in menu" badge */}
                        {suggestion && suggestion.isAlreadyInMenu && (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-800 bg-yellow-100 rounded-full mb-2">
                            {t('menu.alreadyInMenu')}
                          </span>
                        )}

                        {visibleTags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {visibleTags.map((tag) => (
                              <span
                                key={tag.id}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700"
                              >
                                {capitalize(tag.tag)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      {isSelected && (
                        <svg
                          className="w-5 h-5 text-primary-600 ml-2 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-between">
          <button
            type="button"
            onClick={handleClearSelection}
            className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 transition-colors"
          >
            {t('menu.clearSelection')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {t('app.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
